'use client';

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { lchService, type LchRow } from '@/services/lch-service';
import type { LineItem, SubcontractorRow } from '@/types';
import {
  buildSectionRowsWithHD,
  subcontractorCostBySection,
  sumLegacySubcontractors,
  isServiceSubcontractor,
} from '@/lib/section-totals';

/* ── Small helper: text input that commits on blur / Enter ── */
function formatCurrencyInput(n: number): string {
  if (!n && n !== 0) return '';
  const parts = n.toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}

function MarkupInput({
  value,
  onChange,
  placeholder,
  className,
  style,
  formatAsCurrency,
}: {
  value: number;
  onChange: (val: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  formatAsCurrency?: boolean;
}) {
  // Round the % branch to 4dp so a float-derived value never renders as a long decimal
  // (e.g. 20.00000000003). Only affects the displayed value; the user's own typed input
  // is preserved via `local`/commit.
  const toDisplay = (n: number) => formatAsCurrency ? formatCurrencyInput(n) : (n ? String(Math.round(n * 10000) / 10000) : '');
  const [local, setLocal] = useState(toDisplay(value));
  const focusedRef = useRef(false);

  // Sync when external value changes — but NOT while the user is actively typing
  useEffect(() => {
    if (!focusedRef.current) {
      setLocal(toDisplay(value));
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = formatAsCurrency ? e.target.value.replace(/[^0-9.]/g, '') : e.target.value;
    setLocal(raw);
  };

  const commit = () => {
    const stripped = formatAsCurrency ? local.replace(/,/g, '') : local;
    if (formatAsCurrency) {
      const num = parseFloat(stripped) || 0;
      setLocal(formatCurrencyInput(num));
    }
    onChange(stripped);
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={local}
      onChange={handleChange}
      onFocus={(e) => { focusedRef.current = true; e.target.select(); }}
      onBlur={() => { focusedRef.current = false; commit(); }}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
      className={className}
      style={style}
      placeholder={placeholder}
    />
  );
}

interface BidTotalsPanelProps {
  estimateId: string;
  subtotal: number;
  lineItems: LineItem[];
  initialLchData?: LchRow[];
  scopeOfWork?: string;
  onScopeOfWorkChange?: (value: string) => void;
  discountType: 'percentage' | 'flat';
  discountValue: number;
  showDiscountOnProposal: boolean;
  onDiscountTypeChange: (type: 'percentage' | 'flat') => void;
  onDiscountValueChange: (value: number) => void;
  onShowDiscountOnProposalChange: (show: boolean) => void;
  projectConditions?: string;
  additionalNotes?: string;
  onProjectConditionsChange?: (value: string) => void;
  onAdditionalNotesChange?: (value: string) => void;
  priceValidThrough?: string | null;
  onPriceValidThroughChange?: (isoDate: string) => void;
  subcontractorsData?: SubcontractorRow[];
  /**
   * Remove a LEGACY (unassigned) subcontractor entry by id. Legacy entries come from
   * the old standalone tab, which no longer exists, so this is the only way to clear
   * one — e.g. after re-entering it under a service, to avoid counting it twice.
   */
  onRemoveLegacySubcontractor?: (id: string) => void;
  initialMarkupData?: Record<string, { markupPercent: number }[]>;
  onMarkupDataChange?: (data: Record<string, { markupPercent: number }[]>) => void;
  /** Parent-owned ref; the panel stores a synchronous "flush pending markup save" fn here so
   *  the parent can force the debounced markup-% save to commit before generating a proposal. */
  flushMarkupRef?: React.MutableRefObject<(() => void) | null>;
  onComputedTotalChange?: (total: number) => void;
}

interface CostRow {
  category: string;
  subTotal: number;
  markupPercent: number;
  markupAmount: number;
  cost: number;
  isTotal?: boolean;
}

/* ── Section display names ── */
const SECTION_LABELS: Record<string, string> = {
  asphalt: 'Asphalt',
  'asphalt-grading': 'Asphalt/Grading',
  concrete: 'Concrete',
  'concrete-grading': 'Concrete/Grading',
  grading: 'Grading',
  'seal-coat': 'Seal Coat',
  striping: 'Striping',
};

const CATEGORY_LABELS: Record<string, string> = {
  materials: 'Materials',
  equipment: 'Equipment',
  labor: 'Labor',
  misc: 'Miscellaneous',
  crew: 'Crew',
  striping: 'Striping',
};

// buildSectionRows is the canonical grouping — imported from '@/lib/section-totals'
// so the builder, proposal preview, signature page, and backend PDF all agree.

/* ── Build rows for items with no section (legacy / uncategorized) ── */
function buildUnsectionedRows(items: LineItem[]): CostRow[] {
  const unsectioned = items.filter((li) => !li.section);
  if (unsectioned.length === 0) return [];

  const groups: Record<string, number> = {};
  unsectioned.forEach((li) => {
    const cat = li.category || 'misc';
    groups[cat] = (groups[cat] || 0) + li.lineTotal;
  });

  const rows: CostRow[] = Object.entries(groups).map(([cat, total]) => ({
    category: CATEGORY_LABELS[cat] || cat,
    subTotal: total,
    markupPercent: 0,
    markupAmount: 0,
    cost: total,
  }));

  const subTotal = rows.reduce((s, r) => s + r.subTotal, 0);
  rows.push({
    category: 'General Totals',
    subTotal,
    markupPercent: 0,
    markupAmount: 0,
    cost: subTotal,
    isTotal: true,
  });

  return rows;
}

export function BidTotalsPanel(props: BidTotalsPanelProps) {
  const {
    lineItems, scopeOfWork, onScopeOfWorkChange,
    discountType, discountValue, showDiscountOnProposal,
    onDiscountTypeChange, onDiscountValueChange, onShowDiscountOnProposalChange,
    projectConditions, additionalNotes,
    onProjectConditionsChange, onAdditionalNotesChange,
    priceValidThrough, onPriceValidThroughChange,
    subcontractorsData, onRemoveLegacySubcontractor,
    initialMarkupData, onMarkupDataChange,
    onComputedTotalChange,
  } = props;

  /* ── H&D (LCH) data — use pre-fetched data from parent when available ── */
  const [lchRows, setLchRows] = useState<LchRow[]>(props.initialLchData ?? []);

  // Sync when parent updates after H&D calculator saves
  useEffect(() => {
    if (props.initialLchData !== undefined) {
      setLchRows(props.initialLchData);
    }
  }, [props.initialLchData]);

  // Fallback fetch only when parent didn't provide data
  useEffect(() => {
    if (props.initialLchData !== undefined) return;
    if (!props.estimateId) return;
    lchService.getEstimateLchData(props.estimateId).then((data) => {
      if (data) setLchRows(data);
    }).catch(() => {});
  }, [props.estimateId, props.initialLchData]);

  /* ── Per-service subcontractors: cost + bonds by section (shared helper) ── */
  const subcontractorsBySection = useMemo(
    () => subcontractorCostBySection(subcontractorsData || []),
    [subcontractorsData],
  );

  /* ── Discover which sections exist in the line items ── */
  const sections = useMemo(() => {
    const sectionSet = new Set<string>();
    lineItems.forEach((li) => {
      if (li.section) {
        const s = li.section;
        sectionSet.add(
          s === 'asphalt-grading' || s === 'asphalt-rnr' || s === 'asphalt-overlay'
            ? 'asphalt'
            : s === 'concrete-grading'
            ? 'concrete'
            : s,
        );
      }
    });
    // Also include sections that have H&D rows
    lchRows.forEach((r) => {
      if (r.section) sectionSet.add(r.section);
    });
    // Also include services that have subcontractors — a service with only
    // subcontractors still needs its section, or they'd appear nowhere.
    Object.keys(subcontractorsBySection).forEach((s) => sectionSet.add(s));
    return Array.from(sectionSet).sort();
  }, [lineItems, lchRows, subcontractorsBySection]);

  const hasUnsectioned = useMemo(
    () => lineItems.some((li) => !li.section),
    [lineItems],
  );

  /* ── Compute H&D totals per section ── */
  const lchBySection = useMemo(() => {
    const map: Record<string, number> = {};
    for (const row of lchRows) {
      const sec = row.section || '_general';
      map[sec] = (map[sec] || 0) + (row.totalHaulDumpCost || 0);
    }
    return map;
  }, [lchRows]);

  /* ── Legacy (unassigned) subcontractor total ──
     Entries from the old standalone tab: own markup + bonds baked in, summed
     outside every section exactly as before. Service entries are NOT counted
     here — they live in their section's Subcontractors row. */
  const subcontractorTotal = useMemo(
    () => sumLegacySubcontractors(subcontractorsData || []),
    [subcontractorsData],
  );
  const serviceSubcontractorCost = useMemo(
    () => Object.values(subcontractorsBySection).reduce((s, v) => s + v, 0),
    [subcontractorsBySection],
  );
  /** Ids of the legacy entries, in the SAME order (and filter) as the rows of the
   *  '_subcontractors' group below, so row i's remove button removes entry i. */
  const legacySubcontractorIds = useMemo(
    () => (subcontractorsData || [])
      .filter((r) => !isServiceSubcontractor(r))
      .filter((r) => r.company || r.cost > 0)
      .map((r) => r.id),
    [subcontractorsData],
  );

  /* ── Build initial rows per section ──
     Through the SAME helper the proposal preview, signature page and PDF use
     (buildSectionRowsWithHD), so this builder can't drift from what the customer
     sees. It injects each section's Haul & Dump row, then its per-service
     Subcontractors row, before Totals. This panel used to carry a private copy of
     the H&D injection; with a Subcontractors row added, three copies would have
     had to stay in lockstep by hand. */
  const initialSectionRows = useMemo(() => {
    const map: Record<string, CostRow[]> = {};
    sections.forEach((s) => {
      const rows = buildSectionRowsWithHD(
        lineItems, s, lchBySection[s] || 0, subcontractorsBySection[s] || 0,
      );
      if (rows.length > 0) {
        map[s] = rows;
      }
    });
    if (hasUnsectioned) {
      map['_general'] = buildUnsectionedRows(lineItems);
    }
    // Legacy (unassigned) subcontractors from the old standalone tab keep their own
    // group, computed exactly as before. Service subcontractors are already in
    // their section above, so they're excluded here.
    const legacySubcontractors = (subcontractorsData || []).filter((r) => !isServiceSubcontractor(r));
    if (subcontractorTotal > 0 && legacySubcontractors.length > 0) {
      const subRows: CostRow[] = legacySubcontractors
        .filter((r) => r.company || r.cost > 0)
        .map((r) => {
          const cost = r.cost || 0;
          const markup = cost * ((r.markupPercent || 0) / 100);
          const bonds = r.bonds || 0;
          const total = cost + markup + bonds;
          return {
            category: r.company || 'Subcontractor',
            subTotal: total,
            markupPercent: 0,
            markupAmount: 0,
            cost: total,
          };
        });
      const subTotal = subRows.reduce((s, r) => s + r.subTotal, 0);
      subRows.push({
        category: 'Subcontractor Totals',
        subTotal,
        markupPercent: 0,
        markupAmount: 0,
        cost: subTotal,
        isTotal: true,
      });
      map['_subcontractors'] = subRows;
    }
    return map;
  }, [lineItems, sections, hasUnsectioned, lchBySection, subcontractorsBySection, subcontractorsData, subcontractorTotal]);

  /* ── State for markup edits per section ── */
  const [sectionRows, setSectionRows] = useState<Record<string, CostRow[]>>({});

  // Restore saved markupData once initialSectionRows are populated.
  // Use a ref to track the last-seen row count per section so that when the
  // H&D async fetch completes and adds a row, we re-restore from initialMarkupData
  // (preserving existing markup % on the original rows) rather than resetting to 0.
  const restoredRowCountRef = useRef<Record<string, number>>({});
  useEffect(() => {
    if (!initialMarkupData) return;
    const sectionKeys = Object.keys(initialSectionRows);
    if (sectionKeys.length === 0) return; // Wait for data to load

    // Find sections that are new OR whose row count has changed (e.g. H&D row arrived)
    const toRestore: Array<[string, { markupPercent: number }[]]> = [];
    for (const [key, savedRows] of Object.entries(initialMarkupData)) {
      const initial = initialSectionRows[key];
      if (!initial || !savedRows) continue; // not available yet — will retry on next render
      const lastCount = restoredRowCountRef.current[key];
      if (lastCount === initial.length) continue; // already restored at this row count
      toRestore.push([key, savedRows]);
    }
    // Also handle sections present in initialSectionRows but not in initialMarkupData
    // (new sections with no saved markup) whose row count changed
    for (const key of sectionKeys) {
      if (initialMarkupData[key] !== undefined) continue; // handled above
      const initial = initialSectionRows[key];
      if (!initial) continue;
      const lastCount = restoredRowCountRef.current[key];
      if (lastCount === initial.length) continue;
      // No saved markup for this section — just record the count so we don't loop
      restoredRowCountRef.current[key] = initial.length;
    }

    if (toRestore.length === 0) return; // nothing new to restore

    // Record the new row counts before calling setSectionRows
    for (const [key] of toRestore) {
      const initial = initialSectionRows[key];
      if (initial) restoredRowCountRef.current[key] = initial.length;
    }

    setSectionRows((prev) => {
      const restored = { ...prev };
      for (const [key, savedRows] of toRestore) {
        const initial = initialSectionRows[key];
        if (!initial) continue;
        const existing = prev[key];
        // If the estimator has already set a uniform markup on this section, carry it
        // forward to any new rows (e.g. H&D arriving async) rather than resetting to 0.
        const existingNonTotalMarkups = (existing ?? [])
          .filter((r) => !r.isTotal)
          .map((r) => r.markupPercent);
        const uniqueMarkups = new Set(existingNonTotalMarkups);
        const prevailingMp = uniqueMarkups.size === 1 ? existingNonTotalMarkups[0] : 0;

        restored[key] = initial.map((row, i) => {
          const userEdited = existing?.[i];
          // Estimator's value wins — never overwrite a non-zero markup they set
          if (userEdited && userEdited.markupPercent !== 0) {
            return { ...row, markupPercent: userEdited.markupPercent };
          }
          const saved = savedRows[i];
          // For brand-new rows (e.g. H&D that wasn't present when user typed),
          // inherit the section's prevailing markup rather than defaulting to 0.
          const mp = saved?.markupPercent ?? prevailingMp;
          return { ...row, markupPercent: mp };
        });
      }
      return restored;
    });
  }, [initialSectionRows, initialMarkupData]);

  // Persist markup data when it changes — use ref to avoid dependency on inline callback
  const markupSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onMarkupDataChangeRef = useRef(onMarkupDataChange);
  onMarkupDataChangeRef.current = onMarkupDataChange;
  const latestSectionRowsRef = useRef(sectionRows);
  latestSectionRowsRef.current = sectionRows;

  const flushMarkupSave = useCallback(() => {
    if (!onMarkupDataChangeRef.current) return;
    const rows = latestSectionRowsRef.current;
    if (Object.keys(rows).length === 0) return;
    const data: Record<string, { markupPercent: number }[]> = {};
    for (const [key, costRows] of Object.entries(rows)) {
      data[key] = costRows.map(r => ({ markupPercent: r.markupPercent }));
    }
    onMarkupDataChangeRef.current(data);
  }, []);

  // Expose a synchronous markup flush to the parent (via a parent-owned ref) so it can force
  // the debounced markup-% save to persist BEFORE generating / approving / previewing a
  // proposal. The panel is always mounted, so the unmount-flush below never fires on that path
  // — which is how a just-entered markup % could be lost and the proposal priced off stale data.
  useEffect(() => {
    const ref = props.flushMarkupRef;
    if (!ref) return;
    ref.current = flushMarkupSave;
    return () => { ref.current = null; };
  }, [flushMarkupSave]);

  const saveMarkupData = useCallback((rows: Record<string, CostRow[]>) => {
    if (!onMarkupDataChangeRef.current) return;
    if (markupSaveTimer.current) clearTimeout(markupSaveTimer.current);
    markupSaveTimer.current = setTimeout(() => {
      const data: Record<string, { markupPercent: number }[]> = {};
      for (const [key, costRows] of Object.entries(rows)) {
        data[key] = costRows.map(r => ({ markupPercent: r.markupPercent }));
      }
      onMarkupDataChangeRef.current?.(data);
      markupSaveTimer.current = null;
    }, 800);
  }, []);

  // Flush pending save on unmount instead of discarding it
  useEffect(() => {
    return () => {
      if (markupSaveTimer.current) {
        clearTimeout(markupSaveTimer.current);
        markupSaveTimer.current = null;
        flushMarkupSave();
      }
    };
  }, [flushMarkupSave]);

  // Merge: use edited rows if available, otherwise use computed
  const mergedRows = useMemo(() => {
    const result: Record<string, CostRow[]> = {};
    const allKeys = new Set([...Object.keys(initialSectionRows), ...Object.keys(sectionRows)]);
    allKeys.forEach((key) => {
      const initial = initialSectionRows[key];
      const edited = sectionRows[key];
      if (!initial) {
        // Section was removed — skip
        return;
      }
      if (!edited) {
        result[key] = initial;
      } else {
        // Re-apply markups to updated subtotals.
        // When row counts differ (e.g. H&D row arrived after initial restore), match
        // edited markup % by category name so existing edits are not discarded.
        const editedByCategory = new Map(edited.map((r) => [r.category, r.markupPercent]));

        // If all non-total edited rows share the same markup %, new rows that don't
        // appear in edited (e.g. H&D arriving async) inherit it rather than defaulting
        // to 0 and silently lowering the estimator's markup.
        const editedNonTotalMarkups = edited.filter((r) => !r.isTotal).map((r) => r.markupPercent);
        const uniqueEditedMarkups = new Set(editedNonTotalMarkups);
        const prevailingMp = uniqueEditedMarkups.size === 1 ? editedNonTotalMarkups[0] : 0;

        result[key] = initial.map((row, i) => {
          if (row.isTotal) {
            // Recalculate total from non-total rows
            const nonTotalRows = initial.slice(0, -1).map((r, j) => {
              // Prefer same-index match (fast path); fall back to category match, then prevailing
              const byIndex = edited[j];
              const mp = (byIndex?.category === r.category ? byIndex.markupPercent : (editedByCategory.get(r.category) ?? prevailingMp));
              const ma = r.subTotal * (mp / 100);
              return { ...r, markupPercent: mp, markupAmount: ma, cost: r.subTotal + ma };
            });
            const totalSub = nonTotalRows.reduce((s, r) => s + r.subTotal, 0);
            const totalMarkup = nonTotalRows.reduce((s, r) => s + r.markupAmount, 0);
            return {
              ...row,
              subTotal: totalSub,
              // Round to 4dp: this weighted-average % is derived by float division and would
              // otherwise render as a long decimal (e.g. 20.00000000003) after a round-trip.
              // Display-only — the price uses each row's own markup %, never this total-row value.
              markupPercent: totalSub > 0 ? Math.round((totalMarkup / totalSub) * 100 * 10000) / 10000 : 0,
              markupAmount: totalMarkup,
              cost: totalSub + totalMarkup,
            };
          }
          // Prefer same-index match (fast path when lengths are equal); fall back to category match, then prevailing
          const byIndex = edited[i];
          const mp = (byIndex?.category === row.category ? byIndex.markupPercent : (editedByCategory.get(row.category) ?? prevailingMp));
          const ma = row.subTotal * (mp / 100);
          return { ...row, markupPercent: mp, markupAmount: ma, cost: row.subTotal + ma };
        });
      }
    });
    return result;
  }, [initialSectionRows, sectionRows]);

  // Auto-save markup data when sectionRows change
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!initializedRef.current) { initializedRef.current = true; return; }
    saveMarkupData(sectionRows);
  }, [sectionRows, saveMarkupData]);

  /* ── Markup handlers ── */
  const handleMarkupPercentChange = (sectionKey: string, rowIndex: number, value: string) => {
    const percent = parseFloat(value) || 0;
    setSectionRows((prev) => {
      const current = prev[sectionKey] || initialSectionRows[sectionKey] || [];
      const updated = current.map((r, i) =>
        i === rowIndex ? { ...r, markupPercent: percent } : r,
      );
      return { ...prev, [sectionKey]: updated };
    });
  };

  // Apply a uniform markup % to all non-total rows when editing the total row
  const handleTotalMarkupPercentChange = (sectionKey: string, value: string) => {
    const percent = parseFloat(value) || 0;
    // Use initialSectionRows as the authoritative row list — it always includes async rows
    // (e.g. H&D rows fetched after mount). prev[sectionKey] may be stale and missing them,
    // causing H&D to get 0% markup and the displayed total % to drop below what was typed.
    setSectionRows((prev) => {
      const current = initialSectionRows[sectionKey] || prev[sectionKey] || [];
      const updated = current.map((r) =>
        r.isTotal ? r : { ...r, markupPercent: percent },
      );
      return { ...prev, [sectionKey]: updated };
    });
  };

  // Edit the final cost directly — back-calculate markup % from the desired total
  const handleTotalCostChange = (sectionKey: string, value: string) => {
    const newCost = parseFloat(value.replace(/,/g, '')) || 0;
    const rows = mergedRows[sectionKey] || [];
    const totalRow = rows.find(r => r.isTotal);
    const sectionSubTotal = totalRow?.subTotal || 0;
    const newMarkupAmount = newCost - sectionSubTotal;
    const percent = sectionSubTotal > 0 ? (newMarkupAmount / sectionSubTotal) * 100 : 0;
    setSectionRows((prev) => {
      const current = initialSectionRows[sectionKey] || prev[sectionKey] || [];
      const updated = current.map((r) =>
        r.isTotal ? r : { ...r, markupPercent: percent },
      );
      return { ...prev, [sectionKey]: updated };
    });
  };

  // Apply a uniform markup $ by back-calculating % from the section subtotal
  const handleTotalMarkupAmountChange = (sectionKey: string, value: string) => {
    const amount = parseFloat(value) || 0;
    const rows = mergedRows[sectionKey] || [];
    const totalRow = rows.find(r => r.isTotal);
    const sectionSubTotal = totalRow?.subTotal || 0;
    const percent = sectionSubTotal > 0 ? (amount / sectionSubTotal) * 100 : 0;
    setSectionRows((prev) => {
      const current = initialSectionRows[sectionKey] || prev[sectionKey] || [];
      const updated = current.map((r) =>
        r.isTotal ? r : { ...r, markupPercent: percent },
      );
      return { ...prev, [sectionKey]: updated };
    });
  };

  const handleMarkupAmountChange = (sectionKey: string, rowIndex: number, value: string) => {
    const amount = parseFloat(value) || 0;
    setSectionRows((prev) => {
      const rows = mergedRows[sectionKey] || [];
      const row = rows[rowIndex];
      const percent = row && row.subTotal > 0 ? (amount / row.subTotal) * 100 : 0;
      const current = prev[sectionKey] || initialSectionRows[sectionKey] || [];
      const updated = current.map((r, i) =>
        i === rowIndex ? { ...r, markupPercent: percent, markupAmount: amount } : r,
      );
      return { ...prev, [sectionKey]: updated };
    });
  };

  /* ── Total per-row markup from cost breakdown table ── */
  const perRowMarkupTotal = useMemo(() => {
    let total = 0;
    Object.values(mergedRows).forEach((rows) => {
      rows.forEach((r) => {
        if (!r.isTotal) total += r.markupAmount;
      });
    });
    return total;
  }, [mergedRows]);

  /* ── Donut chart data from real line items + H&D + subcontractors ── */
  const donutData = useMemo(() => {
    const groups: Record<string, number> = {
      Materials: 0,
      Equipment: 0,
      Labor: 0,
      Miscellaneous: 0,
      Striping: 0,
      Crew: 0,
    };

    lineItems.forEach((li) => {
      const cat = li.category || 'misc';
      switch (cat) {
        case 'materials': groups['Materials'] += li.lineTotal; break;
        case 'equipment': groups['Equipment'] += li.lineTotal; break;
        case 'labor': groups['Labor'] += li.lineTotal; break;
        case 'misc': groups['Miscellaneous'] += li.lineTotal; break;
        case 'striping': groups['Striping'] += li.lineTotal; break;
        case 'crew': groups['Crew'] += li.lineTotal; break;
        default: groups['Miscellaneous'] += li.lineTotal;
      }
    });

    // Add H&D total
    const totalHD = lchRows.reduce((s, r) => s + (r.totalHaulDumpCost || 0), 0);
    if (totalHD > 0) {
      groups['Haul & Dump'] = totalHD;
    }

    // Subcontractors: legacy entries (own markup + bonds baked in) plus service
    // entries at cost + bonds. Service entries' markup is already in 'Markup'
    // below, via their section's Subcontractors row.
    const allSubcontractors = subcontractorTotal + serviceSubcontractorCost;
    if (allSubcontractors > 0) {
      groups['Subcontractors'] = allSubcontractors;
    }

    // Add per-row markups only (global markup removed)
    if (perRowMarkupTotal > 0) {
      groups['Markup'] = perRowMarkupTotal;
    }

    return Object.entries(groups)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name, value }));
  }, [lineItems, perRowMarkupTotal, lchRows, subcontractorTotal, serviceSubcontractorCost]);

  const donutTotal = useMemo(
    () => donutData.reduce((s, d) => s + d.value, 0),
    [donutData],
  );

  /* ── Discount calculation ── */
  const discountAmount = useMemo(() => {
    if (!discountValue || discountValue <= 0) return 0;
    if (discountType === 'percentage') {
      return donutTotal * (discountValue / 100);
    }
    return discountValue; // flat amount
  }, [discountType, discountValue, donutTotal]);

  const finalTotal = donutTotal - discountAmount;

  // Report computed total (with per-row markup + discount) to parent for sticky footer
  const onComputedTotalChangeRef = useRef(onComputedTotalChange);
  onComputedTotalChangeRef.current = onComputedTotalChange;
  useEffect(() => {
    onComputedTotalChangeRef.current?.(finalTotal);
  }, [finalTotal]);

  // Format currency
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  // Discount uses props from parent (persisted to estimate)

  // Date picker state — initialize from persisted prop; use noon local time to avoid UTC day-shift
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    if (priceValidThrough) return new Date(priceValidThrough);
    return new Date();
  });

  useEffect(() => {
    if (priceValidThrough) setSelectedDate(new Date(priceValidThrough));
  }, [priceValidThrough]);

  // Project Conditions and Additional Notes now come from props (persisted to estimate)

  /* ── Donut Chart ── */
  const DONUT_COLORS: Record<string, string> = {
    Materials: 'var(--blue)',
    Equipment: 'var(--purple, #a855f7)',
    Labor: 'var(--teal, #14b8a6)',
    Miscellaneous: 'var(--orange, #f97316)',
    Striping: 'var(--sky, #0ea5e9)',
    Crew: '#6366f1',
    'Haul & Dump': 'var(--red, #ef4444)',
    Subcontractors: '#f59e0b',
    Markup: 'var(--pink, #ec4899)',
  };

  const DonutChart = () => {
    const [tooltip, setTooltip] = useState<{ label: string; value: string; pct: string; color: string; x: number; y: number } | null>(null);
    const activeSegmentRef = useRef<string | null>(null);
    const wrapRef = React.useRef<HTMLDivElement>(null);

    const radius = 70;
    const circumference = 2 * Math.PI * radius;

    let currentOffset = 0;
    const MIN_SEGMENT_PCT = 1.5; // segments below this % look mangled — skip rendering them
    const segments = donutData.map((item) => {
      const percentage = donutTotal > 0 ? (item.value / donutTotal) * 100 : 0;
      const dashLength = (percentage / 100) * circumference;
      const dashoffset = currentOffset;
      currentOffset += dashLength;
      return {
        ...item,
        color: DONUT_COLORS[item.name] || '#94a3b8',
        percentage,
        dashLength,
        dashoffset,
        visible: percentage >= MIN_SEGMENT_PCT,
      };
    });

    // Hit-test: find which segment the mouse is over based on angle
    const hitTest = useCallback((e: React.MouseEvent) => {
      if (!wrapRef.current) return null;
      const svg = wrapRef.current.querySelector('svg');
      if (!svg) return null;
      const rect = svg.getBoundingClientRect();
      const mx = ((e.clientX - rect.left) / rect.width) * 200;
      const my = ((e.clientY - rect.top) / rect.height) * 200;
      const dx = mx - 100, dy = my - 100;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 50 || dist > 90) return null; // outside the ring
      let angle = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
      if (angle < 0) angle += 360;
      const anglePct = angle / 360;
      let cumPct = 0;
      for (const seg of segments) {
        cumPct += seg.percentage / 100;
        if (anglePct <= cumPct) return seg.visible ? seg : null;
      }
      return segments.filter(s => s.visible).pop() || null;
    }, [segments]);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
      const seg = hitTest(e);
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      if (seg) {
        activeSegmentRef.current = seg.name;
        setTooltip({
          label: seg.name,
          value: formatCurrency(seg.value),
          pct: seg.percentage.toFixed(1) + '%',
          color: seg.color,
          x: e.clientX - rect.left + 12,
          y: e.clientY - rect.top - 40,
        });
      } else {
        activeSegmentRef.current = null;
        setTooltip(null);
      }
    }, [hitTest]);

    const handleMouseLeave = useCallback(() => {
      activeSegmentRef.current = null;
      setTooltip(null);
    }, []);

    if (donutData.length === 0) {
      return (
        <div className="bt-donut-wrap">
          <svg viewBox="0 0 200 200" className="bt-donut">
            <circle cx="100" cy="100" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="40" />
          </svg>
          <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--s400)', marginTop: 8 }}>No line items yet</div>
        </div>
      );
    }

    return (
      <div
        className="bt-donut-wrap"
        ref={wrapRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <svg viewBox="0 0 200 200" className="bt-donut">
          <circle cx="100" cy="100" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="40" />
          {segments.filter(s => s.visible).map((segment, index) => (
            <circle
              key={index}
              className="bt-segment"
              cx="100"
              cy="100"
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeWidth="40"
              strokeDasharray={`${segment.dashLength} ${circumference - segment.dashLength}`}
              strokeDashoffset={-segment.dashoffset}
              transform="rotate(-90 100 100)"
              style={{ pointerEvents: 'none' }}
            />
          ))}
        </svg>
        {tooltip && (
          <div className="bt-tooltip visible" style={{ left: tooltip.x, top: tooltip.y }}>
            <div className="bt-tooltip-label">
              <span className="bt-tooltip-dot" style={{ backgroundColor: tooltip.color }} />
              {tooltip.label}
            </div>
            <div className="bt-tooltip-value">{tooltip.value}</div>
            <div className="bt-tooltip-pct">{tooltip.pct}</div>
          </div>
        )}
      </div>
    );
  };

  /* ── Date Picker ── */
  const DatePicker = () => {
    const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate.getFullYear(), selectedDate.getMonth()));

    const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
    const days = Array(firstDayOfMonth).fill(null).concat(Array.from({ length: daysInMonth }, (_, i) => i + 1));

    const handlePrevMonth = () => {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
    };

    const handleNextMonth = () => {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    };

    const handleSelectDate = (day: number) => {
      // Use noon local time so ISO string never crosses a calendar-day boundary for US timezones
      const picked = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day, 12, 0, 0);
      setSelectedDate(picked);
      onPriceValidThroughChange?.(picked.toISOString());
    };

    const isSelected = (day: number) =>
      day === selectedDate.getDate() &&
      currentMonth.getMonth() === selectedDate.getMonth() &&
      currentMonth.getFullYear() === selectedDate.getFullYear();

    const monthYear = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    return (
      <div className="dp">
        <div className="dp-header">
          <button onClick={handlePrevMonth} className="dp-nav">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <span className="dp-month">{monthYear}</span>
          <button onClick={handleNextMonth} className="dp-nav">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        </div>

        <div className="dp-days-header">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
            <span key={day}>{day}</span>
          ))}
        </div>

        <div className="dp-grid">
          {days.map((day, index) => (
            <button
              key={index}
              onClick={() => day && handleSelectDate(day)}
              disabled={!day}
              className={day ? (isSelected(day) ? 'dp-cell selected' : 'dp-cell') : ''}
              style={!day ? { visibility: 'hidden' } : {}}
            >
              {day}
            </button>
          ))}
        </div>
      </div>
    );
  };

  /* ── Cost Table rendered inline below ── */

  /* ── Ordered section keys ── */
  const orderedKeys = useMemo(() => {
    const keys = sections.slice().sort();
    if (hasUnsectioned) keys.push('_general');
    if (initialSectionRows['_subcontractors']) keys.push('_subcontractors');
    return keys;
  }, [sections, hasUnsectioned, initialSectionRows]);

  return (
    <div className="bt-layout">
      {/* Left Column - Cost Tables */}
      <div className="bt-main">
        {orderedKeys.map((sectionKey) => {
          const sectionRows_ = mergedRows[sectionKey];
          if (!sectionRows_ || sectionRows_.length === 0) return null;
          const title = sectionKey === '_general'
            ? 'General Cost'
            : sectionKey === '_subcontractors'
            ? 'Unassigned Subcontractors'
            : `${SECTION_LABELS[sectionKey] || sectionKey} Cost`;
          return (
            <div key={sectionKey} className="bt-cost-group">
              <h3 className="bt-group-title">{title}</h3>
              {sectionKey === '_subcontractors' && (
                <p className="bt-group-note">
                  Added before subcontractors moved into each service tab. They still
                  count toward the total exactly as before. To change one, add it under
                  its service tab, then remove it here so it isn&apos;t counted twice.
                </p>
              )}
              <table className="bt-table">
                <thead>
                  <tr>
                    <th>Project Category</th>
                    <th>Sub Total</th>
                    <th>Mark Up %</th>
                    <th>Mark Up $</th>
                    <th>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {sectionRows_.map((row, index) => (
                    <tr key={index} className={row.isTotal ? 'bt-totals-row' : ''}>
                      <td style={{ fontWeight: row.isTotal ? 600 : 'normal', color: row.isTotal ? '#111827' : '#374151' }}>
                        {row.category}
                        {sectionKey === '_subcontractors' && !row.isTotal
                          && onRemoveLegacySubcontractor && legacySubcontractorIds[index] && (
                          <button
                            type="button"
                            className="bt-remove-legacy"
                            aria-label={`Remove ${row.category}`}
                            title="Remove this unassigned subcontractor"
                            onClick={() => onRemoveLegacySubcontractor(legacySubcontractorIds[index])}
                          >
                            ×
                          </button>
                        )}
                      </td>
                      <td className="bt-money" style={{ fontWeight: row.isTotal ? 600 : 500, color: row.isTotal ? '#111827' : '#374151' }}>
                        {formatCurrency(row.subTotal)}
                      </td>
                      <td className="bt-money">
                        {row.isTotal ? (
                          <MarkupInput
                            value={row.markupPercent}
                            onChange={(val) => handleTotalMarkupPercentChange(sectionKey, val)}
                            className="bt-input"
                            style={{ textAlign: 'right', fontWeight: 600 }}
                            placeholder="0"
                          />
                        ) : (
                          <MarkupInput
                            value={row.markupPercent}
                            onChange={(val) => handleMarkupPercentChange(sectionKey, index, val)}
                            className="bt-input"
                            style={{ textAlign: 'right' }}
                            placeholder="0"
                          />
                        )}
                      </td>
                      <td className="bt-money">
                        {row.isTotal ? (
                          <MarkupInput
                            value={row.markupAmount}
                            onChange={(val) => handleTotalMarkupAmountChange(sectionKey, val)}
                            className="bt-input"
                            style={{ textAlign: 'right', fontWeight: 600 }}
                            placeholder="0.00"
                          />
                        ) : (
                          <MarkupInput
                            value={row.markupAmount}
                            onChange={(val) => handleMarkupAmountChange(sectionKey, index, val)}
                            className="bt-input"
                            style={{ textAlign: 'right' }}
                            placeholder="0.00"
                          />
                        )}
                      </td>
                      <td className="bt-money">
                        {row.isTotal ? (
                          <span className="bt-cost-wrap">
                            <span className="bt-cost-dollar">$</span>
                            <MarkupInput
                              value={row.cost}
                              onChange={(val) => handleTotalCostChange(sectionKey, val)}
                              className="bt-input bt-input-cost"
                              style={{ textAlign: 'right', fontWeight: 600, color: '#111827' }}
                              placeholder="0.00"
                              formatAsCurrency
                            />
                          </span>
                        ) : (
                          <span style={{ fontWeight: 600, color: '#374151' }}>{formatCurrency(row.cost)}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}

        {orderedKeys.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--s400)', fontSize: 14 }}>
            No line items yet. Add items in the estimate tabs to see cost breakdowns here.
          </div>
        )}

        {/* Scope of Work */}
        <div className="bt-scope-section">
          <label className="bt-scope-label">Scope of Work</label>
          <textarea
            value={scopeOfWork || ''}
            onChange={(e) => onScopeOfWorkChange?.(e.target.value)}
            placeholder="Describe the scope of work for this project. This text will appear in the proposal..."
            rows={6}
            className="bt-textarea"
          />
        </div>
      </div>

      {/* Right Column - Sidebar Cards */}
      <div className="bt-sidebar">
        {/* Total Cost Breakdown Card */}
        <div className="bt-card">
          <div className="bt-card-label">
            Total Cost Breakdown
          </div>
          <div className="bt-card-amount bt-card-amount--tight">
            {formatCurrency(finalTotal)}
          </div>
          {discountAmount > 0 && (
            <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--s400)', marginTop: -4, marginBottom: 4 }}>
              <span style={{ textDecoration: 'line-through' }}>{formatCurrency(donutTotal)}</span>
              {' '}&minus; {discountType === 'percentage' ? `${discountValue}%` : formatCurrency(discountAmount)} discount
            </div>
          )}
          <DonutChart />
        </div>

        {/* Discount Card */}
        <div className="bt-card">
          <div className="bt-card-label bt-card-label--md">Add a Discount?</div>
          <div className="bt-discount-wrap">
            <div className="bt-discount-pair">
              <select
                value={discountType}
                onChange={(e) => onDiscountTypeChange(e.target.value as 'percentage' | 'flat')}
                className="bt-select"
              >
                <option value="percentage">Percentage</option>
                <option value="flat">Flat Amount</option>
              </select>
              <input
                type="number"
                value={discountValue || ''}
                onChange={(e) => onDiscountValueChange(parseFloat(e.target.value) || 0)}
                placeholder={discountType === 'percentage' ? 'Enter %' : 'Enter $'}
                className="bt-discount-input"
              />
            </div>
            <label className="bt-checkbox-label">
              <input
                type="checkbox"
                checked={showDiscountOnProposal}
                onChange={(e) => onShowDiscountOnProposalChange(e.target.checked)}
              />
              <span>Show Discount on Proposal</span>
            </label>
          </div>
        </div>

        {/* Project Conditions Card */}
        <div className="bt-card">
          <div className="bt-card-label bt-card-label--sm">Project Conditions</div>
          <textarea
            value={projectConditions || ''}
            onChange={(e) => onProjectConditionsChange?.(e.target.value)}
            placeholder="Add project conditions..."
            rows={4}
            className="bt-textarea"
          />
        </div>

        {/* Additional Notes Card */}
        <div className="bt-card">
          <div className="bt-card-label bt-card-label--sm">Additional Notes</div>
          <textarea
            value={additionalNotes || ''}
            onChange={(e) => onAdditionalNotesChange?.(e.target.value)}
            placeholder="Add any additional notes to display on the proposal..."
            rows={4}
            className="bt-textarea"
          />
        </div>

        {/* Price Valid Through Card */}
        <div className="bt-card">
          <div className="bt-card-label bt-card-label--md">Price is valid through</div>
          <DatePicker />
        </div>
      </div>
    </div>
  );
}
